'use client'

import { RefObject, useEffect } from 'react'

interface UseScrollChainingOptions {
  /**
   * Whether the wheel listener should be attached.
   * @default true
   */
  enabled?: boolean
  /**
   * Threshold in pixels for detecting top/bottom
   * @default 1
   */
  threshold?: number
  /**
   * Transfer the part of a wheel delta that crosses a boundary to the page
   * during the same wheel event.
   * @default false
   */
  immediate?: boolean
  /**
   * Hand the wheel to the page once a scrollable container hits its top or
   * bottom. Set false to keep the wheel inside the container while it can
   * scroll (pair with `overscroll-contain` so the browser doesn't chain it
   * natively either); a container with nothing to scroll still passes the
   * wheel through to the page. Ignored when `immediate` is set.
   * @default true
   */
  chainAtEdges?: boolean
}

const wheelDeltaInPixels = (event: WheelEvent, pageHeight: number) => {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * pageHeight
  return event.deltaY
}

/**
 * Hook to enable scroll chaining behavior.
 * When the scrollable container reaches its top or bottom boundary,
 * wheel events are explicitly propagated to the body via scrollBy().
 *
 * @param containerTarget - Scrollable container element or its ref
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null)
 * useScrollChaining(scrollRef)
 *
 * return <div ref={scrollRef} className="overflow-auto">...</div>
 * ```
 */
export function useScrollChaining<T extends HTMLElement>(
  containerTarget: RefObject<T | null> | T | null,
  options: UseScrollChainingOptions = {}
) {
  const { enabled = true, threshold = 1, immediate = false, chainAtEdges = true } = options

  useEffect(() => {
    if (!enabled) return

    const container =
      containerTarget && 'current' in containerTarget ? containerTarget.current : containerTarget
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const canScroll = scrollHeight > clientHeight + threshold

      if (immediate) {
        if (e.defaultPrevented || e.ctrlKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return

        const deltaY = wheelDeltaInPixels(e, clientHeight)
        if (deltaY === 0 || !e.cancelable) return

        const maxScrollTop = Math.max(0, scrollHeight - clientHeight)
        const targetScrollTop = scrollTop + deltaY

        // Avoid deriving this branch from floating-point subtraction below:
        // fractional trackpad deltas can leave a tiny non-zero remainder even
        // when the target is fully inside the scroll range.
        if (targetScrollTop >= 0 && targetScrollTop <= maxScrollTop) return

        const nextScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop))
        const consumedDelta = nextScrollTop - scrollTop
        const pageDelta = deltaY - consumedDelta

        // Keep wheel/trackpad movement inside the table on the browser's native,
        // compositor-backed scroll path. Cancelling every event and assigning
        // scrollTop from JavaScript makes dense virtualized tables visibly trail
        // the gesture while the main thread is rendering live cells.
        //
        // We only need to take over the event that actually crosses a boundary,
        // so its remaining delta can be transferred to the page immediately.
        e.preventDefault()
        e.stopPropagation()
        if (consumedDelta !== 0) container.scrollTop = nextScrollTop
        window.scrollBy({ top: pageDelta, behavior: 'auto' })
        return
      }

      // If container is not scrollable, always propagate to window
      if (!canScroll) {
        window.scrollBy({ top: e.deltaY, behavior: 'auto' })
        return
      }

      if (!chainAtEdges) return

      const atTop = scrollTop <= 0
      const atBottom = scrollTop + clientHeight >= scrollHeight - threshold

      // Scrolling UP at the TOP boundary -> scroll window
      if (atTop && e.deltaY < 0) {
        window.scrollBy({ top: e.deltaY, behavior: 'auto' })
        return
      }

      // Scrolling DOWN at the BOTTOM boundary -> scroll window
      if (atBottom && e.deltaY > 0) {
        window.scrollBy({ top: e.deltaY, behavior: 'auto' })
        return
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: !immediate })

    return () => {
      container.removeEventListener('wheel', handleWheel)
    }
  }, [chainAtEdges, containerTarget, enabled, immediate, threshold])
}
